import moment from 'moment'

import { User } from 'hooks/use-user'

import { MIXPANEL_PROPS } from './mixpanel'

// eslint-disable-next-line import/no-cycle
import { getSubscriptionPlanId, getCurrencyAndRegion } from './payment'
import { freeTrialDaysLeft } from './freeVersionBlock'

export const getUserRecurlyPlanName = (user: User) => {
  if (user?.tier === 'free') {
    return 'free'
  }
  if (user?.subscription) {
    const { tier, planInterval } = user.subscription
    if (tier && planInterval) {
      return getSubscriptionPlanId(user.countryCode, tier, planInterval)
    }
  }
  return null
}

export const getUserPlanNameOrTier = (
  user: User,
  TIER_TO_PLAN_NAME_MAP: Record<string, string>,
  isGettingPlanName = true,
) => {
  const { tier } = user
  if (tier && tier === 'free') {
    const daysLeft = freeTrialDaysLeft()
    return daysLeft > 0 ? 'fresh_free' : 'downgraded_free'
  }

  if (user?.subscription) {
    const { tier: subscriptionTier } = user.subscription

    return isGettingPlanName
      ? TIER_TO_PLAN_NAME_MAP[subscriptionTier] || ''
      : subscriptionTier || ''
  }
  return null
}

export const getUserPricingGroup = (user: User) => {
  if (user?.countryCode) {
    const { planCode } = getCurrencyAndRegion(user.countryCode)
    return planCode === 'other' ? 'global' : 'regular'
  }
  return null
}

export const getDaysSinceStartedTrial = (user: User) => {
  if (
    !user ||
    !user.payment_account_created_at ||
    (user.subscription && user.subscription.planInterval !== 'monthly')
  ) {
    return ''
  }

  return moment
    .utc()
    .diff(moment.utc(user.payment_account_created_at * 1000), 'days')
}

export const getStartTrialDate = (user: User) => {
  if (!user || !user.payment_account_created_at) {
    return ''
  }

  return moment.unix(user.payment_account_created_at).utc()
}

export const getSignedUpDate = (user: User) => {
  if (!user || !user.created_at) {
    return ''
  }

  return moment.unix(user.created_at).utc()
}

export const getUserParent = (user: User) => {
  if (
    user?.multi_user_type &&
    Object.values(MIXPANEL_PROPS.USER_PARENT.values).includes(
      user?.multi_user_type,
    )
  ) {
    return user.multi_user_type
  }
  return MIXPANEL_PROPS.USER_PARENT.values.EMPTY
}

export const getUserStatus = (user: User) => {
  const { values } = MIXPANEL_PROPS.USER_STATUS
  if (!user?.subscription) {
    return user ? values.FREE : values.UNLOGGED
  }
  if (
    user.subscription.subscriptionStatus === 'active' ||
    user.subscription.subscriptionStatus === 'paid'
  ) {
    return values.PAID
  }
  if (user.subscription.subscriptionStatus === 'trialing') {
    return values.TRIAL
  }
  return values.UNLOGGED
}

export const getSignInMethod = (user: User) => {
  const { values } = MIXPANEL_PROPS.SIGN_IN_METHOD
  if (!user?.user_type) {
    return ''
  }
  return user.user_type === 'google' ? values.GOOGLE : values.EMAIL
}

export const getDaysSinceSignedUp = (user: User) => {
  if (!user || !user.created_at) {
    return ''
  }
  return moment.utc().diff(moment.utc(user.created_at * 1000), 'days')
}

export const getMonthlyTier1PriceExperimentEnabled = (
  experiment: string | undefined,
  enabled: boolean,
) => {
  if (experiment === 'others') {
    return MIXPANEL_PROPS.MONTHLY_TIER_1_PRICE_EXPERIMENT.values
      .OUT_OF_EXPERIMENT
  }

  return enabled
    ? MIXPANEL_PROPS.MONTHLY_TIER_1_PRICE_EXPERIMENT.values.TRUE
    : MIXPANEL_PROPS.MONTHLY_TIER_1_PRICE_EXPERIMENT.values.FALSE
}

export const getExperimentComparingPlansCheckout = ({
  isPaidUser,
  enabled,
  experiment,
}: {
  isPaidUser: boolean
  enabled: boolean
  experiment: string
}) => {
  if (isPaidUser || experiment === 'others') return null
  return enabled
    ? MIXPANEL_PROPS.EXPERIMENT_COMPARING_PLANS_CHECKOUT.values.TRUE
    : MIXPANEL_PROPS.EXPERIMENT_COMPARING_PLANS_CHECKOUT.values.FALSE
}

export const getUserDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase()
  const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/.test(
    userAgent,
  )

  return isMobile
    ? MIXPANEL_PROPS.USER_DEVICE.values.MOBILE
    : MIXPANEL_PROPS.USER_DEVICE.values.DESKTOP
}
